import React, { Component } from 'react';
import { Box } from 'bloomer';
import { Container } from 'bloomer/lib/layout/Container';
import Conditional from '../components/Conditional';
import LoadingSpinner from '../elements/LoadingSpinner';
import { RouteComponentProps, Link } from '@reach/router';
import { firebaseRef, successAlert, errorAlert } from '../util/helperFunctions';
import User from '../Entities/User';
import { Title } from 'bloomer/lib/elements/Title';
import Bold from '../elements/Bold';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Button } from 'bloomer/lib/elements/Button';
import { Select } from 'bloomer/lib/elements/Form/Select';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import styled from 'styled-components';
import { Section } from 'bloomer/lib/layout/Section';
import { Level } from 'bloomer/lib/components/Level/Level';
import MarginlessTitle from '../elements/MarginlessTitle';
import { LevelRight } from 'bloomer/lib/components/Level/LevelRight';
import { LevelItem } from 'bloomer/lib/components/Level/LevelItem';
import { Image } from 'bloomer/lib/elements/Image';
import Spacer from '../elements/Spacer';
import cat from '../assets/catIcon.svg';
import dog from '../assets/dogIcon.svg';

const SpacedBox = styled(Box)`
  margin: 12px 0;
`;

interface PropTypes extends RouteComponentProps<{}> {}

interface StateTypes {
  isLoading: boolean;
  hasLoaded: boolean;
  users: User[];
  keys: { [Key: string]: string };
  firebase: firebase.database.Reference;
}

class Admin extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    hasLoaded: false,
    isLoading: true,
    users: [],
    keys: {},
    firebase: firebaseRef('/users'),
  };

  fetchUsers = () => {
    this.setState({
      isLoading: true,
    });
    this.state.firebase.once('value', res => {
      if (!res) return;
      const val = res.val() as { [Key: string]: User };
      this.setState({
        users: Object.values(val),
        keys: Object.entries(val).reduce(
          (obj, [key, user]) => ({
            ...obj,
            [user.email]: key,
          }),
          {}
        ),
        isLoading: false,
        hasLoaded: true,
      });
    });
  };

  componentDidMount() {
    this.fetchUsers();
  }

  handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const usersClone = this.state.users.map(user => Object.assign({}, user));
    const newVal = e.target.value as 'disabled' | 'user' | 'power' | 'admin';
    const user = usersClone.find(user => user.email === e.target.id) as User;

    if (!user) {
      return;
    }

    user.admin = newVal;

    this.setState({
      users: usersClone,
    });
  };

  updateUser = async (e: React.FormEvent<HTMLFormElement>, user: User) => {
    e.preventDefault();
    const { keys } = this.state;
    const userRef = firebaseRef(`/users/${keys[user.email]}`);
    const adminLevel = user.admin;
    if (
      adminLevel === 'user' ||
      adminLevel === 'disabled' ||
      adminLevel === 'power' ||
      adminLevel === 'admin'
    ) {
      userRef
        .child('admin') // The level of admin they are
        .set(adminLevel)
        .then(success => {
          successAlert('Success:', 'User Updated Successfully!');
          this.fetchUsers();
        })
        .catch(error => errorAlert(error));
    } else {
      errorAlert('Looks like a bug, contact your supervisor!');
    }
  };

  render() {
    const { isLoading, users, keys, hasLoaded } = this.state;
    // tslint:disable:jsx-no-lambda
    return (
      <Container>
        <Box>
          <Section>
            <Level>
              <MarginlessTitle isSize={1}>
                Welcome to the admin panel
              </MarginlessTitle>
              <LevelRight>
                <LevelItem>
                  <Link to="/admin/dog">
                    <Button isColor="primary">
                      <Image isSize="24x24" src={dog} />{' '}
                      <Spacer>Dog Options</Spacer>
                    </Button>
                  </Link>
                </LevelItem>
                <LevelItem>
                  <Link to="/admin/cat">
                    <Button isColor="primary">
                      <Image isSize="24x24" src={cat} />{' '}
                      <Spacer>Cat Options</Spacer>
                    </Button>
                  </Link>
                </LevelItem>
              </LevelRight>
            </Level>
            <p>
              Below, you can manage users. To manage customizable options for
              specific animal types, select a species.
            </p>
          </Section>
          <Conditional check={hasLoaded} alt={<LoadingSpinner />}>
            <>
              {users.map(user => (
                <form
                  key={keys[user.email]}
                  id={`form-${user.email}`}
                  onSubmit={e => this.updateUser(e, user)}
                >
                  <SpacedBox>
                    <Title>
                      <Bold>User Email:</Bold> {user.email}
                    </Title>
                    <Field disabled={isLoading}>
                      <Label htmlFor={user.email}>User Level:</Label>
                      <Control>
                        <Select
                          disabled={isLoading}
                          isLoading={isLoading}
                          name="formAdminLevel"
                          id={user.email}
                          value={user.admin}
                          onChange={this.handleChange}
                        >
                          <option value="disabled">Disable User</option>
                          <option value="user">Standard User</option>
                          <option value="power">Power User</option>
                          <option value="admin">Admin User</option>
                        </Select>
                      </Control>
                    </Field>
                    <Field>
                      <Control>
                        <Button
                          disabled={isLoading}
                          isLoading={isLoading}
                          type="submit"
                          isColor="primary"
                        >
                          Save
                        </Button>
                      </Control>
                    </Field>
                  </SpacedBox>
                </form>
              ))}
            </>
          </Conditional>
        </Box>
      </Container>
    );
  }

  componentWillUnmount() {
    this.state.firebase.off();
  }
}

export default Admin;
